import { type ReactNode, useId, useState } from 'react';

import { useResponsiveScale } from '../../hooks/useResponsiveRem';
import { Text } from '../../primitives/Text';
import {
  TextInput,
  type TextInputChangeEvent,
  type TextInputFocusEvent,
} from '../../primitives/TextInput';
import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { isTabInitiatedFocus } from '../../utils/keyboardFocus';
import { subTestID } from '../../utils/testId';
import {
  ICON_SIZE_FOR,
  resolveInputIconColor,
  resolveInputPlaceholderColor,
} from './Input.icons';
import type { InputProps } from './Input.types';
import {
  inputAffixTextVariants,
  inputFieldVariants,
  inputFocusRingVariants,
  inputLabelClass,
  inputRingVariants,
  inputRootVariants,
  inputTextVariants,
} from './Input.variants';

/**
 * Cross-platform text Input. Renders identically on web and React Native —
 * an optional label, the bordered field (with icon / affix / trailing-control
 * slots), and an optional helper line. Mirrors the Figma "Flashka Design
 * System" frame `3369:15852` (light) / `4225:13872` (dark); the per-state
 * spec + anatomy live in `Input.variants.ts`.
 *
 * Interaction states are automatic: hover, the focused ring, the darker
 * "active typing" border, and the filled value color all derive from focus +
 * value. Works controlled (`value` + `onChangeText`) or uncontrolled
 * (`defaultValue`).
 *
 * @example
 * ```tsx
 * import { Input } from '@flashka/shared/ui';
 * import { Mail } from '@flashka/shared/ui/icons';
 *
 * <Input label="Email" placeholder="you@example.com" leftIcon={Mail} />
 * <Input label="Username" status="error" helperText="Already taken" />
 * <Input prefix="https://" suffix=".flashka.ai" placeholder="workspace" />
 * ```
 */
export function Input({
  size = 'm',
  label,
  labelTrailing,
  helperText,
  status,
  disabled = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  prefix,
  suffix,
  rightSlot,
  value,
  defaultValue,
  placeholder,
  textAlign = 'left',
  ref,
  onChangeText,
  onFocus,
  onBlur,
  className,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
  nativeInputComponent,
}: InputProps) {
  const { isDark } = useTheme();
  const inputId = useId();
  const helperId = useId();
  const [focused, setFocused] = useState(false);
  // The focus ring is keyboard-only (Figma "Focused"). `keyboardFocus` is set
  // at focus time from whether a Tab keydown is what moved focus here — see
  // `isTabInitiatedFocus` for why Tab, and not "no pointerdown", is the test.
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  // Mirror the value for uncontrolled usage — emptiness drives the
  // Focused (ring) vs Active-typing (dark border) split, per the Figma states.
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
  const hasValue = (value ?? uncontrolledValue).length > 0;

  const state = focused ? (hasValue ? 'typing' : 'focused') : 'rest';
  const showFocusRing = keyboardFocus && !hasValue;
  const iconColor = resolveInputIconColor();
  // Icon px scale with the responsive rem factor (1 on web) — field height,
  // text, and paddings already scale through rem-based classes, so an
  // unscaled icon reads too small next to them on tablets / large font scales.
  const responsiveScale = useResponsiveScale();
  const iconSize = Math.round(ICON_SIZE_FOR[size] * responsiveScale * 1000) / 1000;

  const handleChangeText = (text: string, event: TextInputChangeEvent) => {
    if (value === undefined) setUncontrolledValue(text);
    onChangeText?.(text, event);
  };
  const handleFocus = (event: TextInputFocusEvent) => {
    setFocused(true);
    setKeyboardFocus(isTabInitiatedFocus());
    onFocus?.(event);
  };
  const handleBlur = (event: TextInputFocusEvent) => {
    setFocused(false);
    setKeyboardFocus(false);
    onBlur?.(event);
  };

  // Affixes: strings get the DS affix typography; nodes render as-is (e.g. a
  // composed phone-prefix selector, Figma "Left slot media=Number").
  const renderAffix = (affix: ReactNode) =>
    typeof affix === 'string' || typeof affix === 'number' ? (
      <Text className={inputAffixTextVariants({ size })}>{affix}</Text>
    ) : (
      affix
    );

  return (
    <View className={cn(inputRootVariants({ disabled }), className)} testID={testID}>
      {label ? (
        <View className="flex h-6 w-full flex-row items-center">
          <Text as="label" className={inputLabelClass} webProps={{ htmlFor: inputId }}>
            {label}
          </Text>
          {/* `ml-auto` pushes it to the row's right end whatever the label's
              length; outside the <label>, so it is not part of the name. */}
          {labelTrailing ? <View className="ml-auto">{labelTrailing}</View> : null}
        </View>
      ) : null}

      {/* Field host anchors the absolute focus-ring overlay; the field itself
          stays a constant size so focusing never reflows its content. */}
      <View className={inputRingVariants({ size })}>
        <View
          className={inputFieldVariants({
            size,
            state,
            status: status ?? 'none',
            leftInset: LeftIcon ? 'icon' : 'text',
            rightInset: rightSlot ? 'slot' : RightIcon ? 'icon' : 'text',
          })}>
          {LeftIcon ? (
            // Logical margin on web: in a right-to-left field (onboarding in
            // Hebrew) the icon sits at the right and its gap faces the text.
            <View className="shrink-0 native:mr-1.5 web:me-1.5">
              <LeftIcon color={iconColor} size={iconSize} />
            </View>
          ) : null}
          {prefix != null ? (
            <>
              {renderAffix(prefix)}
              <View className="mx-2 w-px shrink-0 self-stretch bg-secondary-300" />
            </>
          ) : null}

          <TextInput
            ref={ref}
            testID={subTestID(testID, 'input')}
            className={inputTextVariants({ size, focused, align: textAlign })}
            value={value}
            defaultValue={defaultValue}
            placeholder={placeholder}
            placeholderTextColor={resolveInputPlaceholderColor(focused, isDark)}
            onChangeText={handleChangeText}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            accessibilityLabel={accessibilityLabel ?? label}
            webProps={{
              id: inputId,
              'aria-invalid': status === 'error' || undefined,
              'aria-describedby': helperText ? helperId : undefined,
              ...webProps,
            }}
            nativeProps={nativeProps}
            nativeInputComponent={nativeInputComponent}
          />

          {suffix != null ? (
            <>
              <View className="mx-2.5 w-px shrink-0 self-stretch bg-secondary-300" />
              {renderAffix(suffix)}
            </>
          ) : null}
          {RightIcon ? (
            <View className="shrink-0 native:ml-1 web:ms-1">
              <RightIcon color={iconColor} size={iconSize} />
            </View>
          ) : null}
          {rightSlot ? (
            <View className="shrink-0 native:ml-1 web:ms-1">{rightSlot}</View>
          ) : null}
        </View>
        {showFocusRing ? (
          <View
            className={inputFocusRingVariants({ size })}
            nativeProps={{ pointerEvents: 'none' }}
          />
        ) : null}
      </View>

      {helperText ? (
        <Text
          className={cn(
            'w-full font-medium text-b3 native:font-mona-medium',
            status === 'error'
              ? 'text-red-600'
              : status === 'success'
                ? 'text-green-600'
                : 'text-secondary-400',
          )}
          testID={subTestID(testID, 'helper')}
          webProps={{ id: helperId }}>
          {helperText}
        </Text>
      ) : null}
    </View>
  );
}
(Input as { displayName?: string }).displayName = 'Input';
